import classes from "./ManageFilterTags.module.scss";
import React, { ChangeEvent, Fragment, useCallback, useEffect, useState } from "react";
import { IconSecondaryButton } from "components/core/IconSecondaryButton";
import plusIcon from "components/icons/plusIcon.svg";
import closeIcon from "components/icons/closeWhite.svg";
import SecondaryButton from "components/core/SecondaryButton";
import { useTagsQuery } from "queries";
import { ITag } from "components/tags/types";
import RemovableTag from "components/core/tags/RemovableTag";
import Tag from "components/core/tags/Tag";

interface IProps {
  addedTags?: string[];
  onChange: (tags: string[]) => void;
}

const SHOW_COUNT_TAGS = 15;

export const ManageFilterTags = (props: IProps) => {
  const { addedTags, onChange } = props;

  const [isAddingMode, setAddingMode] = useState(false);
  const [currentValue, setCurrentValue] = useState("");
  const [tags, setTags] = useState<ITag[]>([]);
  const [filteredTags, setFilteredTags] = useState<string[]>([]);

  const { isSuccess, data } = useTagsQuery();

  const handleEnableAddingMode = useCallback(() => {
    setAddingMode(true);
  }, []);

  const handleDisableAddingMode = useCallback(() => {
    setAddingMode(false);
    setCurrentValue("");
  }, []);

  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setCurrentValue(event.currentTarget.value);
  }, []);

  const handleAddedTag = useCallback((tag: string) => () => {
    if (addedTags && addedTags.indexOf(tag) !== -1) {
      return;
    }

    const newTags = addedTags || [];
    newTags.push(tag);

    onChange(newTags);
    handleDisableAddingMode();
  }, [addedTags, onChange, handleDisableAddingMode]);

  const handleRemoveTag = useCallback((tag: string) => () => {
    if (!addedTags) {
      return;
    }

    const index = addedTags.indexOf(tag);

    if (index === -1) {
      return;
    }

    const newTags = [...addedTags];
    newTags.splice(index, 1);

    onChange(newTags);
  }, [addedTags, onChange]);

  useEffect(() => {
    if (currentValue.length > 0) {
      const newTags = tags.filter((el: ITag) => {
        if (addedTags && addedTags.indexOf(el.name) !== -1) {
          return false;
        }

        return el.name.toLowerCase().includes(currentValue.toLowerCase());
      });
      setFilteredTags(newTags.map(newTag => newTag.name).slice(0, SHOW_COUNT_TAGS));
    } else {
      setFilteredTags([]);
    }
  }, [currentValue, tags, addedTags]);

  useEffect(() => {
    if (isSuccess && data) {
      setTags(data.tags);
    }
  }, [isSuccess, data]);

  return (
    <div className={classes.manageFilterTags}>
      <div className={classes.filterTags}>
        {
          addedTags && addedTags.map((addedTag) => {
            return <RemovableTag key={addedTag} tag={addedTag} onRemove={handleRemoveTag}/>;
          })
        }

        {
          isAddingMode ? (
            <SecondaryButton className={classes.inputButton}>
              <input className={classes.input} type="text" value={currentValue} onChange={handleChange} />
              <div className={classes.remove} onClick={handleDisableAddingMode}>
                <img className={classes.removeIcon} src={closeIcon} alt="" />
              </div>
            </SecondaryButton>
          ) : (
            <Fragment>
              <IconSecondaryButton url={plusIcon} variant="active" onClick={handleEnableAddingMode}/>
              {
                addedTags && addedTags.length === 0 && (
                  <div className={classes.placeholder}>Search by Tag across your content</div>
                )
              }
            </Fragment>
          )
        }
      </div>
      {
        isAddingMode && filteredTags.length > 0 ? (
          <div className={classes.availableTags}>
            {
              filteredTags.map(filteredTag => {
                return <Tag key={filteredTag} tag={filteredTag} onClick={handleAddedTag(filteredTag)} />;
              })
            }
          </div>
        ) : null
      }
    </div>
  );
};
